import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { STAGES, CHANNELS, CHANNEL_LABELS, LIFECYCLE_LABELS, lifecycleOf, type Channel } from '@joker/domain'
import {
  CURRENCY_LABELS,
  PAYMENT_CURRENCIES,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type PaymentCurrency,
  type PaymentMethod,
} from '@joker/contracts'
import { useTRPC } from '../trpc'
import { displayPhone } from '../phone'
import { Panel, StatusChip, Loading, ErrorNote, fmtDate, DateParts } from '../ui'
import { LeadSections, EmailHistory, askedTime } from './Prospects'
import { PersonOrders, isDemo } from './Purchases'
import { ScannedPrescriptions } from './ScannedPrescriptions'
import { Programs } from './Program'
import { PersonTags } from './TagPicker'

/**
 * Oryx never stored a creation date. Its developer is explicit about it: the
 * optometrist-form date only exists for accounts made after that form shipped,
 * older accounts were backfilled with a fake one, and the first-session date
 * moves *forward* when somebody is teleported back to the start or archived.
 * So every date here is the earliest we can prove rather than the day somebody
 * signed up, and it is labelled so nobody mistakes a floor for a fact.
 */
const CREATED_SOURCE: Record<string, string> = {
  account: 'from the account record',
  'first session': 'earliest session — the account record is later',
  migration: 'the Cortex import, not a signup',
  reset: 'training was reset; anything before it is gone',
  unknown: 'no date recorded',
}

const MARK: Record<string, string> = {
  order: 's1',
  refund: 's2',
  session: '',
  assessment: 's3',
  stage: '',
  email: '',
  signup: '',
  account: '',
  note: '',
}

/**
 * One page per human, whatever stage they are at: the lead they started as
 * (what they asked and when), what they bought, their prescription, the
 * programs they trained on, and everything recorded about them. Leads,
 * Purchases and Patients all open this, at /people/:id.
 */
export function Person({
  id,
  onBack,
  onOpenPerson,
  onOpenOrder,
  onOpenProgram,
  backLabel = 'Patients',
}: {
  id: string
  onBack: () => void
  onOpenPerson?: (id: string) => void
  onOpenOrder?: (id: string) => void
  onOpenProgram?: (id: string) => void
  /** Where Back goes, named: a record opened from a practice returns there. */
  backLabel?: string
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const q = useQuery(trpc.people.byId.queryOptions({ id }))
  const lead = useQuery(trpc.prospects.byId.queryOptions({ id }))

  const [saved, setSaved] = useState(false)
  const edit = useMutation(
    trpc.peopleAdmin.edit.mutationOptions({
      /* Awaited, and the record refetched before the editor closes. Closing
         first showed the panel straight from cache, so a saved date appeared
         not to have changed until something else happened to refresh it. */
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.people.byId.queryKey({ id }) })
        await queryClient.invalidateQueries()
        setEditing(false)
        setSaved(true)
        window.setTimeout(() => setSaved(false), 2000)
      },
    }),
  )
  const [editing, setEditing] = useState(false)
  const examiners = useQuery(trpc.optometrists.examiners.queryOptions())
  /* The confirmed practices, for assigning this patient to one. The same list
     the Optometrists screen shows, so a practice that is not listed there is not
     offered here either. */
  const practices = useQuery(trpc.optometrists.list.queryOptions())
  const assign = useMutation(
    trpc.optometrists.setPatientPractice.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.people.byId.queryKey({ id }) })
        await queryClient.invalidateQueries()
      },
    }),
  )
  const pay = useMutation(
    trpc.peopleAdmin.recordPayment.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.people.byId.queryKey({ id }) })
        await queryClient.invalidateQueries()
      },
    }),
  )

  const setEmployee = useMutation(
    trpc.peopleAdmin.setEmployee.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(),
    }),
  )

  const setVip = useMutation(
    trpc.peopleAdmin.setVip.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(),
    }),
  )

  const setOptometrist = useMutation(
    trpc.peopleAdmin.setOptometrist.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(),
    }),
  )

  const setTablet = useMutation(
    trpc.peopleAdmin.setTablet.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(),
    }),
  )

  const setTest = useMutation(
    trpc.peopleAdmin.setTest.mutationOptions({
      onSuccess: () => {
        /* Marking changes who this person is to every other screen, so the
           people list and the bucket counts are both stale now. */
        void queryClient.invalidateQueries()
      },
    }),
  )

  /* Declared before the early returns: a hook after one runs on some renders and
     not others, and React tears the component down when the count changes —
     which is exactly what happened here the moment the query resolved.
     Null means "follow what is stored"; ticking it takes over. */
  const [paidOverride, setPaidOverride] = useState<boolean | null>(null)
  const [refundRequested, setRefundRequested] = useState<boolean | null>(null)
  const [amount, setAmount] = useState<string | null>(null)
  const [currency, setCurrency] = useState<PaymentCurrency | null>(null)
  const [method, setMethod] = useState<PaymentMethod | null>(null)

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="this patient" />

  const p = q.data

  const hasPaid = paidOverride ?? p.paidAt != null
  /* A refund has been asked for if one is recorded, until somebody says
     otherwise — so an existing refund date keeps its box ticked. */
  const wantsRefund = refundRequested ?? p.refundedAt != null
  const amountValue = amount ?? (p.paidCents ? String(p.paidCents / 100) : '')
  const currencyValue: PaymentCurrency =
    currency ??
    ((PAYMENT_CURRENCIES as readonly string[]).includes(p.paidCurrency ?? '')
      ? (p.paidCurrency as PaymentCurrency)
      : 'USD')
  const methodValue: PaymentMethod =
    method ??
    ((PAYMENT_METHODS as readonly string[]).includes(p.paymentMethod ?? '')
      ? (p.paymentMethod as PaymentMethod)
      : p.practiceName
        ? 'optometrist'
        : 'wix')
  const setHasPaid = (v: boolean) => setPaidOverride(v)
  const stage = STAGES.find((s) => s.id === p.stage)

  return (
    <>
      <button className="back" onClick={onBack}>
        ← {backLabel}
      </button>
      <h2 className="view-title">{p.name ?? 'Unnamed patient'}</h2>
      <p className="view-sub">
        <span className={`pill life-${lifecycleOf(p.stage)}`}>{LIFECYCLE_LABELS[lifecycleOf(p.stage)]}</span>{' '}
        {stage?.label ?? p.stage}
        {lead.data?.code && <> · {lead.data.code}</>}
        {lead.data?.joinedAt && (
          <>
            {' '}· asked {fmtDate(lead.data.joinedAt)}
            {askedTime(lead.data.joinedAt) && <>, {askedTime(lead.data.joinedAt)}</>}
          </>
        )}
      </p>
      <p className="view-sub">
        {p.email ?? 'no email on file'}
        {p.oryxUserNumber != null && <> · Oryx ID <strong>{p.oryxUserNumber}</strong></>}
        {isDemo(p.accountStatus) && (
          <>
            {' '}
            <span className="pill demo" title="Account made at purchase; turned into a real one once their details are in">
              Demo
            </span>
          </>
        )}{' '}
        ·{' '}
        <span className="mono">{p.id}</span>
      </p>

      <div className="test-mark">
        <button type="button" className="btn" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Close editor' : saved ? 'Saved ✓' : 'Edit record'}
        </button>
        <label>
          <input
            type="checkbox"
            checked={p.isTest}
            /* A name-matched account is hidden by a rule about its name, not by
               a mark on the person. Unticking would write is_test = false, look
               like it worked and change nothing — so it is not offered. */
            disabled={setTest.isPending || (p.isTest && !p.testMarked)}
            onChange={(e) => setTest.mutate({ id: p.id, isTest: e.target.checked })}
          />
          <span>Test user</span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={p.isEmployee}
            /* Same reasoning as the test mark: a name carrying "(EMP)" is
               labelled by a rule, not by a mark anyone can lift here. */
            disabled={setEmployee.isPending || (p.isEmployee && !p.employeeMarked)}
            onChange={(e) => setEmployee.mutate({ id: p.id, isEmployee: e.target.checked })}
          />
          <span>Employee</span>
        </label>
        {/* No rule can set this one, so it is never disabled: a VIP is somebody
            a person decided to look after, and only a person can undecide it. */}
        <label>
          <input
            type="checkbox"
            checked={p.isVip}
            disabled={setVip.isPending}
            onChange={(e) => setVip.mutate({ id: p.id, isVip: e.target.checked })}
          />
          <span>VIP</span>
        </label>
        {/* The other side of the deal. Ticking it also clears what this person
            is to a practice, since a partner optometrist is not their own
            practice's patient — which is what had two of them counted as
            paying customers with no payment behind either. */}
        <label>
          <input
            type="checkbox"
            checked={p.isOptometrist}
            disabled={setOptometrist.isPending}
            onChange={(e) => setOptometrist.mutate({ id: p.id, isOptometrist: e.target.checked })}
          />
          <span>Optometrist</span>
        </label>
        <span className="muted">
          {p.isTest && !p.testMarked
            ? 'Hidden automatically — the name contains “test”, so there is no mark to remove.'
            : p.isTest
              ? 'Hidden from every screen. Reachable through the Test users bucket.'
              : 'Hides this patient everywhere except the Test users bucket.'}
        </span>
      </div>

      {/* Hand-set tags, from the managed list: see TagPicker. */}
      <PersonTags personId={p.id} />

      {/* Each block loads on its own, so one that fails says so in its own
          place and the rest of the page still shows. */}
      {lead.error ? (
        <>
          <ErrorNote error={lead.error} />
          {/* The readings stand on their own if the record around them fails. */}
          <ScannedPrescriptions personId={p.id} />
        </>
      ) : (
        lead.data && (
          <LeadSections
            prospect={lead.data}
            onChanged={() => void queryClient.invalidateQueries()}
            {...(onOpenOrder ? { onOpenOrder } : {})}
          />
        )
      )}
      <PersonOrders personId={p.id} {...(onOpenOrder ? { onOpenOrder } : {})} />
      <EmailHistory prospectId={p.id} />

      <Programs personId={p.id} {...(onOpenProgram ? { onOpenProgram } : {})} />

      <div className="grid-2">
        <Panel title="Timeline" note="orders · sessions · assessments · stage changes">
          <ul className="tl">
            {p.timeline.map((e, i) => (
              <li key={i}>
                <span className={`mk ${MARK[e.kind] ?? ''}`} />
                <strong>{e.title}</strong>
                {e.detail && (
                  <>
                    <br />
                    <span className="muted">{e.detail}</span>
                  </>
                )}
                <time>{fmtDate(e.at)}</time>
              </li>
            ))}
            {p.timeline.length === 0 && <li className="muted">Nothing recorded yet.</li>}
          </ul>
        </Panel>

        {editing && p && (
          <Panel title="Edit record" note="manual correction">
            <form
              className="edit-form"
              onSubmit={(e) => {
                e.preventDefault()
                const f = new FormData(e.currentTarget)
                const paid = String(f.get('paidAt') ?? '')
                const refunded = String(f.get('refundedAt') ?? '')
                const chosen = String(f.get('practiceId') ?? '') || null
                if (chosen !== (p.practiceId ?? null)) {
                  assign.mutate({ personId: p.id, practiceId: chosen })
                }
                edit.mutate({
                  id: p.id,
                  firstName: String(f.get('firstName') ?? '').trim() || null,
                  lastName: String(f.get('lastName') ?? '').trim() || null,
                  /* A cleared date field is an instruction to clear the value,
                     not an instruction to leave it alone — so it sends null. */
                  paidAt: paid ? new Date(paid + 'T12:00:00Z').toISOString() : null,
                  refundedAt: refunded ? new Date(refunded + 'T12:00:00Z').toISOString() : null,
                  channel: String(f.get('channel')) as Channel,
                  channelDetail: String(f.get('channelDetail') ?? '').trim() || null,
                  examinedBy: String(f.get('examinedBy') ?? '').trim() || null,
                  /* Midday, so the day cannot shift under a timezone conversion. */
                  firstTouchAt: (() => {
                    const d = String(f.get('firstTouchAt') ?? '').trim()
                    return d ? new Date(d + 'T12:00:00Z').toISOString() : null
                  })(),
                })

                /* The amount is an order, not a column on the person, so it is a
                   second call — and only when there is an amount to record. */
                const value = Number(amountValue.replace(/[^\d.]/g, ''))
                if (hasPaid && paid && Number.isFinite(value) && value > 0) {
                  pay.mutate({
                    personId: p.id,
                    amount: value,
                    currency: currencyValue,
                    method: methodValue,
                    paidOn: new Date(paid + 'T12:00:00Z').toISOString(),
                  })
                }
              }}
            >
              <label>
                <span>First name</span>
                <input type="text" name="firstName" maxLength={120} defaultValue={p.firstName ?? ''} />
              </label>
              <label>
                <span>Last name</span>
                <input type="text" name="lastName" maxLength={120} defaultValue={p.lastName ?? ''} />
              </label>
              <label>
                <span>Created</span>
                {/* Oryx goes back to 2021, so the years run that far. */}
                <DateParts
                  name="firstTouchAt"
                  earliestYear={2021}
                  value={(p.signupAt ?? p.createdAt ?? '').slice(0, 10) || null}
                />
              </label>
              {/* Has this person paid at all, and only then when. A bare date box
                  made "no" and "we have not recorded it" the same empty field,
                  and an optometrist's own demo account ended up with a payment
                  date because nothing ever asked the first question. */}
              <label className="ctl check inline-check">
                <input
                  type="checkbox"
                  name="hasPaid"
                  checked={hasPaid}
                  onChange={(e) => setHasPaid(e.target.checked)}
                />
                <span>Has paid</span>
              </label>
              {hasPaid && (
                <>
                  <label>
                    <span>Paid on</span>
                    {/* Defaults to the day the record was created: that is the
                        best available answer for a purchase nobody dated, and it
                        beats opening on today for money taken months ago.
                        Required, because ticked-but-blank would save as "not
                        paid", which is not what ticking it said. */}
                    <DateParts
                      name="paidAt"
                      value={(p.paidAt ?? p.createdAt ?? '').slice(0, 10) || null}
                      required
                    />
                  </label>
                  <label>
                    <span>Amount</span>
                    <input
                      inputMode="decimal"
                      value={amountValue}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="1000"
                    />
                  </label>
                  <label>
                    <span>Currency</span>
                    <select
                      value={currencyValue}
                      onChange={(e) => setCurrency(e.target.value as PaymentCurrency)}
                    >
                      {PAYMENT_CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {CURRENCY_LABELS[c]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Payment channel</span>
                    <select
                      value={methodValue}
                      onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {PAYMENT_METHOD_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {/* A refund presupposes a payment; offering it to somebody who has
                  paid nothing invites a record that cannot be true. */}
              {hasPaid && (
                <label className="ctl check inline-check">
                  <input
                    type="checkbox"
                    checked={wantsRefund}
                    onChange={(e) => setRefundRequested(e.target.checked)}
                  />
                  <span>Requested a refund</span>
                </label>
              )}
              {hasPaid && wantsRefund && (
                <label>
                  <span>Refunded on</span>
                  <DateParts name="refundedAt" value={p.refundedAt?.slice(0, 10) ?? null} />
                </label>
              )}
              <label>
                <span>Optometrist</span>
                {/* Saved by its own mutation on submit, not through the patch:
                    assigning a practice also locks it against the next Oryx
                    import and sets the channel, and that belongs in one place
                    rather than duplicated into the person update. */}
                <select name="practiceId" defaultValue={p.practiceId ?? ''}>
                  <option value="">None — not a practice patient</option>
                  {(practices.data ?? []).map((pr) => (
                    <option key={pr.id} value={pr.id}>
                      {pr.practiceName ?? pr.name}
                      {pr.locationName ? ` — ${pr.locationName}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Prescription optometrist</span>
                <select name="examinedBy" defaultValue={p.examinedBy ?? ''}>
                  {/* Blank falls back to the practice's own optometrist, which is
                      right for most people and wrong for anyone attributed to a
                      practice they were never examined at. */}
                  <option value="">Whoever the practice says</option>
                  {(examiners.data ?? []).map((who) => (
                    <option key={who} value={who}>
                      {who}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Source</span>
                <select name="channel" defaultValue={p.channel}>
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {CHANNEL_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Campaign or referrer</span>
                <input
                  type="text"
                  name="channelDetail"
                  maxLength={200}
                  placeholder="spring-newsletter, Dr Aecherli, promo code…"
                  defaultValue={p.channelDetail ?? ''}
                />
              </label>
              <p className="footnote">
                Setting a source marks it as human-set, so the next Oryx sync will not overwrite
                it. Changing the payment date re-runs this patient&rsquo;s stage.
              </p>
              <div className="edit-actions">
                <button type="submit" className="btn primary" disabled={edit.isPending}>
                  {edit.isPending ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </Panel>
        )}

        <Panel title="Record">
          <dl className="kv">
            <dt>Stage</dt>
            <dd>
              {stage?.label} <StatusChip severity={stage?.severity ?? null} />
            </dd>
            <dt>Paid on</dt>
            <dd className="num">{fmtDate(p.paidAt)}</dd>
            <dt>Prescription by</dt>
            <dd>{p.examinedBy ?? <span className="muted">—</span>}</dd>
            <dt>Paid via</dt>
            <dd>
              {p.paymentMethod ? (
                (PAYMENT_METHOD_LABELS[p.paymentMethod as PaymentMethod] ?? p.paymentMethod)
              ) : (
                <span className="muted">—</span>
              )}
            </dd>
            <dt>Created</dt>
            <dd className="num">
              {fmtDate(p.createdAt)}
              <span className="muted sub-line"> · {CREATED_SOURCE[p.createdSource]}</span>
            </dd>
            <dt>Source</dt>
            <dd>
              {CHANNEL_LABELS[p.channel]}
              {p.channelDetail
                ? ` — ${p.channelDetail}`
                : p.channel === 'optometrist' && p.practiceName
                  ? ` — ${p.practiceName}`
                  : ''}
              {p.channelLocked && <span className="muted sub-line"> · set by hand</span>}
            </dd>
            <dt>Age</dt>
            <dd className="num">
              {p.age ?? '—'}
              {p.ageRange && <span className="muted sub-line"> · {p.ageRange}</span>}
            </dd>
            <dt>Born</dt>
            <dd className="num">{p.dateOfBirth ? fmtDate(p.dateOfBirth) : '—'}</dd>
            <dt>Reading Rx</dt>
            <dd className="num">{p.rx ? `${p.rx} D` : '—'}</dd>
            <dt>Sessions</dt>
            <dd className="num">{p.sessionsTotal}</dd>
            {/* The programme they were assigned, which is 29, 39 or 49 rather
                than the flat 30 this used to print for everybody. */}
            <dt>Program</dt>
            <dd className="num">
              {p.essentialsSessions ? `${p.essentialsSessions} sessions` : '—'}
              {p.essentialsClass && <span className="muted sub-line"> · {p.essentialsClass}</span>}
            </dd>
            <dt>Cadence</dt>
            <dd className="num">{p.sessionsPerWeek == null ? '—' : `${p.sessionsPerWeek} / week`}</dd>
            <dt>Last session</dt>
            <dd className="num">{fmtDate(p.lastSessionAt)}</dd>
            <dt>Acuity change</dt>
            <dd className="num">
              {p.acuityDeltaLines == null ? 'not yet assessed' : `+${p.acuityDeltaLines} lines`}
            </dd>
            <dt>Email opens 30d</dt>
            <dd className="num">{p.emailOpens30d}</dd>
            <dt>Phone</dt>
            {/* Shown the way the phone itself would show it; the stored value
                stays E.164 so it is still diallable from anywhere. */}
            {/* Not monospace: a formatted number already groups its digits, and
                the fixed-width font pushed the brackets and spaces apart. */}
            <dd>{displayPhone(p.phone) || '—'}</dd>
            <dt>Identities</dt>
            <dd className="mono" style={{ fontSize: 12 }}>
              {p.identities.map((i) => i.source).join(' · ') || '—'}
            </dd>
          </dl>
        </Panel>
      </div>

      <Panel title="Tablet" note="hardware we lent them">
        <div className="tablet-row">
          <label>
            <input
              type="checkbox"
              checked={p.tabletReceived}
              disabled={setTablet.isPending}
              onChange={(e) =>
                setTablet.mutate({
                  id: p.id,
                  received: e.target.checked,
                  /* Un-ticking "received" clears the return with it: a tablet
                     that never went out cannot have come back. */
                  returned: e.target.checked && p.tabletReturned,
                  returnedAt: e.target.checked ? p.tabletReturnedAt : null,
                })
              }
            />
            <span>Received a tablet</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={p.tabletReturned}
              disabled={setTablet.isPending}
              onChange={(e) =>
                setTablet.mutate({
                  id: p.id,
                  /* Returning implies having received one. */
                  received: true,
                  returned: e.target.checked,
                  returnedAt: e.target.checked ? p.tabletReturnedAt : null,
                })
              }
            />
            <span>Returned it</span>
          </label>
          {p.tabletReturned && (
            <label className="pfield">
              <span>Returned on</span>
              {/* Three lists cannot be half-typed. The date input this replaced
                  reported every partial value, so typing a year saved 0002 on
                  the way to 2026 — Heather Griffith-Tesch still carries one. */}
              <DateParts
                value={p.tabletReturnedAt}
                earliestYear={2021}
                disabled={setTablet.isPending}
                onChange={(iso) =>
                  setTablet.mutate({ id: p.id, received: true, returned: true, returnedAt: iso })
                }
              />
            </label>
          )}
        </div>
        <p className="footnote">
          The date is optional. Fifty patients are known to have returned a tablet and not one of
          them has a date recorded, so requiring one would mean inventing fifty. This used to live
          in the patient&rsquo;s name as &ldquo;(RETURN)&rdquo;, which made the name wrong on every
          screen and the fact impossible to count.
        </p>
      </Panel>

      <Training id={id} />
    </>
  )
}

/**
 * The training history, session by session, with the exercise blocks inside each.
 *
 * Its own query rather than part of the person payload: a heavy trainer has
 * hundreds of sessions and several thousand blocks, and the record header should
 * not wait for them.
 */
export function Training({ id }: { id: string }) {
  const trpc = useTRPC()
  const [open, setOpen] = useState<string | null>(null)
  const [limit, setLimit] = useState(20)
  const q = useQuery(trpc.people.sessions.queryOptions({ id }))

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return null

  const { sessions, etests } = q.data
  if (sessions.length === 0 && etests.length === 0) return null

  const mins = (secs: number | null) => (secs == null ? '—' : `${Math.round(secs / 60)}m`)
  const acc = (r: number | null, w: number | null) =>
    r == null || w == null || r + w === 0 ? '—' : `${Math.round((r / (r + w)) * 100)}%`

  return (
    <>
      <Panel title="Training sessions" note={`${sessions.length.toLocaleString()} recorded`}>
        <table className="data">
          <thead>
            <tr>
              <th className="num">#</th>
              <th className="num">Started</th>
              <th className="num">Length</th>
              <th className="num">Blocks</th>
              <th>Stage</th>
              <th>App</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.slice(0, limit).map((sn) => (
              <Fragment key={sn.id}>
                <tr
                  tabIndex={0}
                  onClick={() => setOpen(open === sn.id ? null : sn.id)}
                  onKeyDown={(e) => e.key === 'Enter' && setOpen(open === sn.id ? null : sn.id)}
                >
                  <td className="num">{sn.number ?? '—'}</td>
                  <td className="num">{fmtDate(sn.startedAt)}</td>
                  <td className="num">{mins(sn.durationSeconds)}</td>
                  <td className="num">{sn.blocks.length || <span className="muted">—</span>}</td>
                  <td>
                    {sn.trainingStage ?? '—'}
                    {sn.segmentSeq != null && (
                      <span className="muted sub-line">
                        {' '}
                        · segment {sn.segmentSeq}
                        {sn.segmentSession != null ? `/${sn.segmentSession}` : ''}
                      </span>
                    )}
                  </td>
                  <td className="muted">{sn.appVersion ?? '—'}</td>
                  <td className="num muted">{open === sn.id ? '▾' : '▸'}</td>
                </tr>
                {open === sn.id && (
                  <tr className="detail">
                    <td colSpan={7}>
                      {sn.blocks.length === 0 ? (
                        <p className="footnote">
                          No block results recorded for this session — it predates the results the
                          dashboard keeps, or the session was abandoned before the first block ended.
                        </p>
                      ) : (
                        <table className="data inner">
                          <thead>
                            <tr>
                              <th>Block</th>
                              <th className="num">Right</th>
                              <th className="num">Wrong</th>
                              <th className="num">Accuracy</th>
                              <th className="num">Trials</th>
                              <th className="num">Reversals</th>
                              <th className="num">Threshold</th>
                              <th className="num">Score</th>
                              <th>Trend</th>
                              <th>Ended</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sn.blocks.map((b) => (
                              <tr key={b.blockId}>
                                <td>{b.name ?? `block ${b.blockId}`}</td>
                                <td className="num">{b.rightAnswers ?? '—'}</td>
                                <td className="num">{b.wrongAnswers ?? '—'}</td>
                                <td className="num">{acc(b.rightAnswers, b.wrongAnswers)}</td>
                                <td className="num">{b.trials ?? '—'}</td>
                                <td className="num">{b.reversals ?? '—'}</td>
                                <td className="num">{b.threshold == null ? '—' : b.threshold.toFixed(2)}</td>
                                <td className="num">
                                  {b.thresholdScore == null ? '—' : b.thresholdScore.toFixed(2)}
                                </td>
                                <td className="muted">{b.trend ?? '—'}</td>
                                <td className="muted">{b.endReason ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {sessions.length > limit && (
          <p className="footnote">
            <button type="button" className="linkish" onClick={() => setLimit(limit + 50)}>
              Show 50 more
            </button>{' '}
            — {(sessions.length - limit).toLocaleString()} older sessions not shown.
          </p>
        )}
      </Panel>

      {etests.length > 0 && (
        <Panel title="Eye tests" note={`${etests.length} taken between segments`}>
          <table className="data">
            <thead>
              <tr>
                <th className="num">Taken</th>
                <th className="num">After session</th>
                <th className="num">Acuity</th>
                <th className="num">Threshold</th>
                <th className="num">Accuracy</th>
                <th className="num">Reaction</th>
                <th className="num">Distance</th>
              </tr>
            </thead>
            <tbody>
              {etests.map((e) => (
                <tr key={e.id}>
                  <td className="num">{fmtDate(e.takenAt)}</td>
                  <td className="num">{e.afterSessionNumber ?? '—'}</td>
                  <td className="num">{e.acuity == null ? '—' : e.acuity.toFixed(2)}</td>
                  <td className="num">{e.threshold == null ? '—' : e.threshold.toFixed(2)}</td>
                  <td className="num">{acc(e.rightAnswers, e.wrongAnswers)}</td>
                  <td className="num">{e.reactionMs == null ? '—' : `${Math.round(e.reactionMs)} ms`}</td>
                  <td className="num">{e.sittingDistance == null ? '—' : `${e.sittingDistance} cm`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </>
  )
}
